const QUOTE = '"';
const DELIMITERS = [',', ';', '\t'] as const;
const BYTE_ORDER_MARK = '﻿';

export type CsvDelimiter = (typeof DELIMITERS)[number];

const countOutsideQuotes = (line: string, delimiter: string): number => {
  let inQuotes = false;
  let count = 0;
  for (const char of line) {
    if (char === QUOTE) inQuotes = !inQuotes;
    if (!inQuotes && char === delimiter) count += 1;
  }
  return count;
};

export const detectDelimiter = (text: string): CsvDelimiter => {
  const firstLine = text.replace(BYTE_ORDER_MARK, '').split(/\r?\n/u)[0] ?? '';
  return DELIMITERS.reduce<CsvDelimiter>(
    (best, candidate) =>
      countOutsideQuotes(firstLine, candidate) > countOutsideQuotes(firstLine, best) ? candidate : best,
    ',',
  );
};

type ReaderState = { rows: string[][]; row: string[]; cell: string; inQuotes: boolean };

const endCell = (state: ReaderState): void => {
  state.row.push(state.cell);
  state.cell = '';
};

const endRow = (state: ReaderState): void => {
  endCell(state);
  state.rows.push(state.row);
  state.row = [];
};

const readQuoted = (state: ReaderState, char: string, next: string | undefined): number => {
  if (char !== QUOTE) {
    state.cell += char;
    return 1;
  }
  if (next === QUOTE) {
    state.cell += QUOTE;
    return 2;
  }
  state.inQuotes = false;
  return 1;
};

const readPlain = (state: ReaderState, char: string, delimiter: CsvDelimiter): void => {
  if (char === QUOTE) state.inQuotes = true;
  else if (char === delimiter) endCell(state);
  else if (char === '\n') endRow(state);
  else if (char !== '\r') state.cell += char;
};

export const parseCsv = (text: string, delimiter: CsvDelimiter): string[][] => {
  const source = text.replace(BYTE_ORDER_MARK, '');
  const state: ReaderState = { rows: [], row: [], cell: '', inQuotes: false };
  let index = 0;
  while (index < source.length) {
    const char = source.charAt(index);
    if (state.inQuotes) index += readQuoted(state, char, source[index + 1]);
    else {
      readPlain(state, char, delimiter);
      index += 1;
    }
  }
  if (state.cell !== '' || state.row.length > 0) endRow(state);
  return state.rows.filter((row) => row.some((cell) => cell.trim() !== ''));
};
